import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Movie } from '../../types';
import { movieService, watchPartyService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import SubscriptionModal from '../../components/SubscriptionModal';
import './MovieDetail.css';

const MovieDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user, subscription, checkSubscription } = useAuth();
  
  const [movie, setMovie] = useState<Movie | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [creatingParty, setCreatingParty] = useState(false);

  // Format duration
  const formatDuration = (mins: number | string | undefined) => {
    if (mins === undefined || mins === null) return '—';
    const m = parseInt(String(mins));
    if (!m || isNaN(m)) return '—';
    const h = Math.floor(m / 60);
    const min = m % 60;
    return h > 0 ? `${h}h ${min}m` : `${min}m`;
  };

  // Get thumbnail URL
  const getThumbnailUrl = (thumbnail: string) => {
    if (!thumbnail) return '/public/assets/images/no-image.jpg';
    if (thumbnail.startsWith('http://') || thumbnail.startsWith('https://')) {
      return thumbnail;
    }
    return `/public/uploads/thumbnails/${thumbnail}`;
  };

  // Check access permission
  const hasAccess = (): boolean => {
    if (!isAuthenticated) return false;
    if (user?.role === 'admin') return true;
    return subscription?.has_subscription === true;
  };

  // Load movie details
  useEffect(() => {
    const loadMovie = async () => {
      if (!id) {
        setError('Invalid movie ID');
        setLoading(false);
        return;
      }

      // Check authentication first
      if (!isAuthenticated) {
        navigate(`/login?redirect=/movies/${id}`);
        return;
      }

      try {
        setLoading(true);
        
        // Check subscription for non-admin users
        if (user?.role !== 'admin') {
          await checkSubscription();
          
          if (!subscription?.has_subscription) {
            setShowSubscriptionModal(true);
            setLoading(false);
            return;
          }
        }

        const movieData = await movieService.getMovie(parseInt(id));
        setMovie(movieData);
        setError(null);
      } catch (err: any) {
        console.error('Error loading movie:', err);
        setError(err.message || 'Failed to load movie');
      } finally {
        setLoading(false);
      }
    };

    loadMovie();
  }, [id, isAuthenticated, user, subscription, navigate, checkSubscription]);

  // Watch movie handler
  const handleWatchMovie = () => {
    if (!hasAccess()) {
      setShowSubscriptionModal(true);
      return;
    }
    
    if (movie) {
      navigate(`/watch/${movie.id}`);
    }
  };

  // Create watch party handler
  const handleCreateParty = async () => {
    if (!hasAccess()) {
      setShowSubscriptionModal(true);
      return;
    }

    if (!movie) return;

    try {
      setCreatingParty(true);
      const party = await watchPartyService.createParty(
        movie.id,
        `${movie.title} - Watch Party`,
        10
      );
      
      navigate(`/room/${party.room_code}`);
    } catch (err: any) {
      console.error('Error creating party:', err);
      alert('Failed to create watch party: ' + err.message);
    } finally {
      setCreatingParty(false);
    }
  };

  // Subscription modal with redirect
  if (showSubscriptionModal && !hasAccess()) {
    return (
      <div className="min-h-screen bg-netflix-darker">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-lg mx-auto text-center">
            <div className="bg-gradient-to-br from-netflix-red/20 to-red-600/10 p-8 rounded-2xl border-2 border-netflix-red/30">
              <i className="fas fa-lock text-5xl text-netflix-red mb-6"></i>
              <h2 className="text-2xl font-bold text-white mb-4">🔒 Subscription Required</h2>
              <p className="text-gray-400 mb-6">
                Please activate a subscription to watch movies
              </p>
              <Link
                to="/subscribe"
                className="block w-full py-4 bg-gradient-to-r from-netflix-red to-red-600 rounded-xl text-lg font-bold mb-4 hover:opacity-90 transition-opacity"
              >
                🎁 Start 14-Day Free Trial
              </Link>
              <Link
                to="/"
                className="text-gray-500 hover:text-white transition-colors"
              >
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-netflix-darker flex items-center justify-center">
        <div className="text-center">
          <div className="loading mb-4"></div>
          <p className="text-netflix-light-gray">Loading movie...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !movie) {
    return (
      <div className="min-h-screen bg-netflix-darker">
        <div className="container mx-auto px-4 py-12 text-center">
          <i className="fas fa-exclamation-triangle text-6xl text-netflix-red mb-4"></i>
          <h2 className="text-2xl font-bold text-white mb-4">Movie Not Found</h2>
          <p className="text-gray-400 mb-6">{error || 'The requested movie could not be found.'}</p>
          <Link
            to="/movies"
            className="inline-block px-6 py-3 bg-netflix-red hover:bg-red-700 rounded-lg font-bold transition-colors"
          >
            Back to Movies
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-netflix-darker">
      <div className="container mx-auto px-4 lg:px-8 py-8">
        <div className="movie-detail-grid">
          {/* Movie Poster */}
          <div className="movie-poster-container">
            <div className="relative">
              <img
                src="/public/assets/images/logoformovies.png"
                alt="CineClubs"
                className="movie-thumbnail-logo absolute top-3 left-3 w-12 h-12 z-10"
              />
              <img
                src={getThumbnailUrl(movie.thumbnail)}
                alt={movie.title}
                className="w-full rounded-xl shadow-2xl"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (!target.dataset.tried) {
                    target.dataset.tried = 'true';
                    target.src = '/public/assets/images/no-image.jpg';
                  }
                }}
              />
            </div>
          </div>

          {/* Movie Info */}
          <div className="movie-info-container">
            <h1 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              {movie.title}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap gap-4 mb-6 text-gray-400">
              <span className="movie-meta-item bg-netflix-red/20 px-4 py-2 rounded-lg">
                {movie.genre}
              </span>
              <span className="movie-meta-item">
                <i className="fas fa-clock mr-2"></i>
                {formatDuration(movie.duration)}
              </span>
              <span className="movie-meta-item">
                <i className="fas fa-calendar mr-2"></i>
                {movie.release_year}
              </span>
              <span className="movie-meta-item inline-flex items-center bg-yellow-400 text-black px-3 py-2 rounded-lg font-bold">
                <span className="font-black mr-2">IMDb</span>
                <i className="fas fa-star mr-1"></i>
                {movie.rating}/10
              </span>
            </div>

            {/* Description */}
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              {movie.description}
            </p>

            {/* Stats */}
            <div className="bg-white/5 rounded-xl p-4 mb-6">
              <p className="text-gray-400">
                <i className="fas fa-eye mr-2"></i>
                <strong className="text-white">{movie.view_count?.toLocaleString() || 0}</strong> views
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleWatchMovie}
                className="flex-1 py-4 px-8 bg-netflix-red hover:bg-red-700 rounded-xl text-lg font-bold transition-all flex items-center justify-center gap-2"
              >
                <i className="fas fa-play"></i>
                Watch Now
              </button>
              <button
                onClick={handleCreateParty}
                disabled={creatingParty}
                className="flex-1 py-4 px-8 bg-white/10 hover:bg-white/20 disabled:opacity-50 rounded-xl text-lg font-bold transition-all flex items-center justify-center gap-2"
              >
                {creatingParty ? (
                  <>
                    <div className="loading w-5 h-5"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-users"></i>
                    Create Watch Party
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <SubscriptionModal onClose={() => setShowSubscriptionModal(false)} />
      )}
    </div>
  );
};

export default MovieDetail;
